/**
 * Поля формы, выводимые из реестра типов.
 *
 * Ни одна форма не описывает поля конкретного типа вручную: элемент управления
 * выбирается по `kind` свойства, подпись и подсказка берутся из реестра.
 * Добавление свойства в реестр сразу даёт рабочее поле во всех формах.
 */

import { useMemo, useState } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController, useWatch } from 'react-hook-form';
import {
  CURRENCIES,
  CURRENCY_SYMBOLS,
  formatMoney,
  parseAmountToMinor,
  type Currency,
  type PropertyDefinition,
  type UserDto,
} from '@project-os/contracts';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/** Значение «не задано» для необязательного поля. */
const EMPTY = '__none__';

export interface PropertyFieldProps<T extends FieldValues> {
  control: Control<T>;
  /** Полный путь в форме, например `properties.url`. */
  name: Path<T>;
  property: PropertyDefinition;
  members?: UserDto[];
  /** Путь к полю валюты — нужен денежному полю для форматирования. */
  currencyName?: Path<T>;
  disabled?: boolean;
}

function FieldShell({
  id,
  property,
  error,
  children,
}: {
  id: string;
  property: PropertyDefinition;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="gap-1">
        {property.labelRu}
        {property.required ? (
          <span className="text-destructive" aria-hidden>
            *
          </span>
        ) : null}
      </Label>
      {children}
      {property.hintRu && !error ? (
        <p className="text-xs leading-relaxed text-muted-foreground">{property.hintRu}</p>
      ) : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

export function PropertyField<T extends FieldValues>({
  control,
  name,
  property,
  members = [],
  currencyName,
  disabled,
}: PropertyFieldProps<T>) {
  const { field, fieldState } = useController({ control, name });
  const id = `field-${String(name).replace(/\./g, '-')}`;
  const error = fieldState.error?.message;

  const currency = useWatch({
    control,
    name: (currencyName ?? name) as Path<T>,
    disabled: property.kind !== 'money',
  }) as Currency | undefined;

  switch (property.kind) {
    case 'text':
      return (
        <FieldShell id={id} property={property} error={error}>
          <Textarea
            id={id}
            rows={3}
            disabled={disabled}
            value={(field.value as string | null) ?? ''}
            onChange={(event) => field.onChange(event.target.value || null)}
            onBlur={field.onBlur}
            aria-invalid={Boolean(error)}
          />
        </FieldShell>
      );

    case 'boolean':
      return (
        <div className="flex items-center gap-2">
          <Checkbox
            id={id}
            disabled={disabled}
            checked={Boolean(field.value)}
            onCheckedChange={(checked) => field.onChange(checked === true)}
          />
          <Label htmlFor={id} className="font-normal">
            {property.labelRu}
          </Label>
        </div>
      );

    case 'enum': {
      const options = property.options ?? [];
      return (
        <FieldShell id={id} property={property} error={error}>
          <Select
            disabled={disabled}
            value={(field.value as string | null) ?? EMPTY}
            onValueChange={(value) => field.onChange(value === EMPTY ? null : value)}
          >
            <SelectTrigger id={id} className="w-full" aria-invalid={Boolean(error)}>
              <SelectValue placeholder="Не выбрано" />
            </SelectTrigger>
            <SelectContent>
              {!property.required ? <SelectItem value={EMPTY}>Не выбрано</SelectItem> : null}
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.labelRu}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldShell>
      );
    }

    case 'currency':
      return (
        <FieldShell id={id} property={property} error={error}>
          <Select
            disabled={disabled}
            value={(field.value as string | null) ?? 'EUR'}
            onValueChange={field.onChange}
          >
            <SelectTrigger id={id} className="w-full" aria-invalid={Boolean(error)}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((code) => (
                <SelectItem key={code} value={code}>
                  {code} · {CURRENCY_SYMBOLS[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldShell>
      );

    case 'money':
      return (
        <FieldShell id={id} property={property} error={error}>
          <MoneyInput
            id={id}
            disabled={disabled}
            amountMinor={field.value as number | null}
            currency={currency ?? 'EUR'}
            onChange={field.onChange}
            invalid={Boolean(error)}
          />
        </FieldShell>
      );

    case 'userRefList':
      return (
        <FieldShell id={id} property={property} error={error}>
          <UserMultiSelect
            members={members}
            value={(field.value as string[] | null) ?? []}
            onChange={field.onChange}
            disabled={disabled}
          />
        </FieldShell>
      );

    case 'integer':
    case 'decimal':
      return (
        <FieldShell id={id} property={property} error={error}>
          <Input
            id={id}
            type="number"
            inputMode="decimal"
            step={property.kind === 'integer' ? 1 : 'any'}
            disabled={disabled}
            value={field.value === null || field.value === undefined ? '' : String(field.value)}
            onChange={(event) =>
              field.onChange(event.target.value === '' ? null : Number(event.target.value))
            }
            onBlur={field.onBlur}
            aria-invalid={Boolean(error)}
          />
        </FieldShell>
      );

    case 'date':
    case 'datetime':
      return (
        <FieldShell id={id} property={property} error={error}>
          <Input
            id={id}
            type={property.kind === 'date' ? 'date' : 'datetime-local'}
            disabled={disabled}
            value={toInputDate(field.value, property.kind)}
            onChange={(event) => field.onChange(event.target.value || null)}
            onBlur={field.onBlur}
            aria-invalid={Boolean(error)}
          />
        </FieldShell>
      );

    case 'url':
    case 'email':
    case 'string':
    default:
      return (
        <FieldShell id={id} property={property} error={error}>
          <Input
            id={id}
            type={property.kind === 'email' ? 'email' : property.kind === 'url' ? 'url' : 'text'}
            inputMode={property.kind === 'url' ? 'url' : undefined}
            placeholder={property.kind === 'url' ? 'https://' : undefined}
            maxLength={property.maxLength}
            disabled={disabled}
            value={(field.value as string | null) ?? ''}
            onChange={(event) => field.onChange(event.target.value || null)}
            onBlur={field.onBlur}
            aria-invalid={Boolean(error)}
          />
        </FieldShell>
      );
  }
}

function toInputDate(value: unknown, kind: 'date' | 'datetime'): string {
  if (typeof value !== 'string' || value === '') return '';
  if (kind === 'date') return value.slice(0, 10);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 16);
}

/**
 * Ввод суммы.
 *
 * Пользователь видит «12,50», а в форму записывается целое число минорных
 * единиц. Плавающая точка в денежных значениях не используется нигде.
 */
function MoneyInput({
  id,
  amountMinor,
  currency,
  onChange,
  disabled,
  invalid,
}: {
  id: string;
  amountMinor: number | null;
  currency: Currency;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const formatted = useMemo(
    () =>
      amountMinor === null || amountMinor === undefined
        ? ''
        : formatMoney({ amountMinor, currency }, { withSymbol: false }),
    [amountMinor, currency],
  );

  // Пока поле в фокусе, показывается введённый текст: иначе форматирование
  // переставляло бы курсор на каждом нажатии.
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <div className="relative">
      <Input
        id={id}
        inputMode="decimal"
        disabled={disabled}
        aria-invalid={invalid}
        className="pr-9"
        value={draft ?? formatted}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          if (next.trim() === '') {
            onChange(null);
            return;
          }
          const minor = parseAmountToMinor(next, currency);
          if (minor !== null) onChange(minor);
        }}
        onBlur={() => setDraft(null)}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground"
      >
        {CURRENCY_SYMBOLS[currency]}
      </span>
    </div>
  );
}

function UserMultiSelect({
  members,
  value,
  onChange,
  disabled,
}: {
  members: UserDto[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}) {
  if (members.length === 0) {
    return <p className="text-xs text-muted-foreground">Участников проекта пока нет</p>;
  }

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((item) => item !== id) : [...value, id]);
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {members.map((member) => {
        const active = value.includes(member.id);
        return (
          <button
            key={member.id}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => toggle(member.id)}
            className={cn(
              'rounded-md border px-2 py-1 text-xs transition-colors disabled:opacity-50',
              active
                ? 'border-primary bg-primary/10 text-foreground'
                : 'text-muted-foreground hover:bg-accent',
            )}
          >
            {member.name}
          </button>
        );
      })}
    </div>
  );
}
